/* ============
 * Main File
 * ============
 *
 * Will initialize the application.
 */

import { createApp } from 'vue';
import * as Sentry from '@sentry/vue';
import { ElNotification } from 'element-plus';
import VueScrollTo from 'vue-scrollto';
import VueVirtualScroller from 'vue-virtual-scroller';
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';
import { PerfectScrollbarPlugin } from 'vue3-perfect-scrollbar';
import 'vue3-perfect-scrollbar/style.css';
import 'ladda/dist/ladda.min.css';
import pinia from '@/stores/pinia';
import element from '@/plugins/element';
import router from '@/plugins/router';
import mixpanel from '@/plugins/mixpanel';
import BrandPlugin from '@/plugins/brand';
import Auth0Plugin from '@/auth0';
import Formatters from '@/utils/formatters';
import bus from '@/utils/eventBus/bus';
import MediumEditor from '@/components/Global/MediumEditor';
import 'medium-editor/dist/css/medium-editor.css';
import TextareaAutosize from '@/components/Global/TextareaAutosize';
import { domain, clientId } from '../auth0_config.json';
import {
  i18n,
  isNavigationPending,
  reloadForStaleChunk,
} from './bootstrap';
import App from './app';

// Vite failed to preload a chunk of a stale build (a deploy renewed the hashed filenames):
// covers the dynamic imports fired outside the router, such as lazy modals
// (EditSingleJobModal). A failing navigation is left to `router.onError`, which knows the
// target route. `preventDefault()` only once the reload is under way, otherwise the import
// would silently resolve to `undefined` and fail further down with no reload.
window.addEventListener('vite:preloadError', (event) => {
  if (isNavigationPending()) {
    return;
  }

  const { pathname, search, hash } = window.location;
  if (reloadForStaleChunk(pathname + search + hash)) {
    event.preventDefault();
  }
});

const app = createApp(App);

if (process.env.LOG_SENTRY === true) {
  Sentry.init({
    app,
    dsn: 'https://d50155c9a9d1474aaf51999f5ebae104@o75476.ingest.us.sentry.io/237056',
    // The SDK wraps global callbacks, so a crash inside an embedded third-party
    // bundle is reported against our project with no frame of ours in it
    // (SP-14072 / Sentry CUSTOMER-5CG). We cannot fix code we do not ship.
    denyUrls: [/^https?:\/\/js\.intercomcdn\.com\//],
    // Benign browser notification raised when a ResizeObserver callback
    // defers its remaining notifications to the next frame. It is emitted by
    // Element's ResizeObserver-based components on the checkout summary page and
    // has no functional impact, so it must not be reported (SP-14120).
    ignoreErrors: [/ResizeObserver loop/],
    // Component props carry customer data (names, emails, addresses).
    attachProps: false,
    // Keep printing Vue errors to the console, as Vue's default handler did.
    logErrors: true,
  });
}

Object.assign(app.config.globalProperties, {
  $bus: bus,
  $mixpanel: mixpanel,
  $notify: ElNotification,
});

app.use(pinia);
app.use(router);
app.use(i18n);
app.use(element);
app.use(Formatters);
app.use(BrandPlugin, { i18n: i18n.global });
app.use(Auth0Plugin, { domain, clientId });
app.use(VueScrollTo);
app.use(VueVirtualScroller);
app.use(PerfectScrollbarPlugin);

app.component('medium-editor', MediumEditor);
app.component('textarea-autosize', TextareaAutosize);

app.mount('#app');
